import { supabase } from '../../supabase';
import { Paper, PaperAuthor } from '@/types/paper';
import { toast } from 'sonner';
import { mapToPaper } from '../paper-mappers';
import { v4 as uuidv4 } from 'uuid';
import { adminLog, adminError } from '@/lib/utils/admin-logger';
import { validatePaperInputs } from './validation';
import { verifyAuthAndAuthor, getAuthorData } from './auth-checks';
import { createPaperVersion } from './create-paper-version';
import { convertArxivUrlToPdf, isArxivUrl } from '@/lib/utils/arxiv-utils';
import { ensureBotUserExists } from '../bot-user';
import { validateArxivPaperLicense } from '@/lib/services/license-validation';
import { SITE_URL } from '@/lib/constants/urls';
import { ArxivLicense } from '@/types/license';
import { initProcessingJobs, DEFAULT_UPLOAD_STEPS } from '@/lib/api/processing/initJobs';
import { isGdprObjectionSkip, showAIObjectionToast } from '@/lib/ai/objectionNotice';

export async function createPaper(
  title: string,
  abstract: string,
  authorId: string,
  category: string,
  keywords: string[],
  fileUrl?: string,
  externalUrl?: string,
  additionalAuthors: PaperAuthor[] = [],
  visibility: 'public' | 'private' = 'public',
  requestReview: boolean = false,
  selectedReviewers: string[] = [],
  publishedAt?: string,
  /**
   * Explicit override for whether the title is a placeholder. When omitted we
   * fall back to "full extraction implies a placeholder title". Callers that
   * let the user type a title while leaving the abstract to the AI (Writer
   * publishing) pass `false` so a real title is never overwritten.
   */
  titleIsPlaceholderOverride?: boolean

): Promise<Paper | null> {
  try {
    // Input validation
    if (!validatePaperInputs(title, abstract, authorId, category)) {
      return null;
    }

    // Auth and author verification
    if (!await verifyAuthAndAuthor(authorId)) {
      return null;
    }

    // Get author data
    const authorData = await getAuthorData(authorId);
    if (!authorData) {
      return null;
    }

    // Check AI review availability for user-uploaded papers (any visibility).
    // ArXiv auto-imports are exempt and stay free.
    const isUserUpload = !(externalUrl && isArxivUrl(externalUrl));
    if (isUserUpload) {
      const { data: canRequest, error: checkError } = await supabase.rpc('can_request_ai_review', {
        user_id_param: authorId
      });

      if (checkError || !canRequest) {
        toast.error('AI Review Limit Reached', {
          description: 'You have reached your monthly AI review limit. Buy more credits or wait for next month.',
          duration: 8000
        });
        return null;
      }
    }


    // License validation for arXiv papers
    let licenseData: {
      license?: ArxivLicense;
      licenseComplianceStatus: 'compliant' | 'non_compliant' | 'pending' | 'unknown';
      licenseFetchedAt?: string;
      licenseValidationError?: string;
    } = {
      licenseComplianceStatus: 'unknown'
    };

    if (externalUrl && isArxivUrl(externalUrl)) {
      try {
        adminLog('Validating license for arXiv paper:', externalUrl);
        
        const licenseResult = await validateArxivPaperLicense(externalUrl);
        
        if (licenseResult.isValid && licenseResult.license) {
          licenseData = {
            license: licenseResult.license,
            licenseComplianceStatus: licenseResult.isCompliant ? 'compliant' : 'non_compliant',
            licenseFetchedAt: new Date().toISOString()
          };
          
          adminLog('License validation successful:', {
            name: licenseResult.license.name,
            compliant: licenseResult.isCompliant
          });
        } else {
          // License validation failed - this should block paper creation for non-compliant licenses
          if (!licenseResult.isCompliant) {
            const errorMessage = licenseResult.errors[0] || 'License validation failed';
            
            toast.error('License Not Compliant', {
              description: errorMessage,
              duration: 8000
            });
            
            adminError('Paper creation blocked due to non-compliant license:', {
              url: externalUrl,
              errors: licenseResult.errors
            });
            
            return null;
          } else {
            // License fetch failed but license might be compliant - allow creation with warning
            licenseData = {
              licenseComplianceStatus: 'pending',
              licenseValidationError: licenseResult.errors[0] || 'License validation failed',
              licenseFetchedAt: new Date().toISOString()
            };
            
            adminLog('License validation failed but proceeding with paper creation:', licenseResult.errors);
          }
        }
      } catch (error) {
        // License validation error - log but don't block paper creation
        adminError('License validation error:', error);
        licenseData = {
          licenseComplianceStatus: 'pending',
          licenseValidationError: `License validation error: ${error instanceof Error ? error.message : 'Unknown error'}`,
          licenseFetchedAt: new Date().toISOString()
        };
      }
    }

    // Handle author list creation
    let authors: PaperAuthor[] = [];
    
    if (additionalAuthors && additionalAuthors.length > 0) {
      // Use the provided authors list (e.g., from arXiv import)
      authors = [...additionalAuthors];
      
      // Check if the current user is already in the authors list by name
      const currentUserAuthorIndex = authors.findIndex(
        author => author.name.toLowerCase() === authorData.name.toLowerCase()
      );
      
      if (currentUserAuthorIndex >= 0) {
        // Update the existing author entry with platform user data
        authors[currentUserAuthorIndex] = {
          ...authors[currentUserAuthorIndex],
          id: authorId,
          isPlatformUser: true,
          institution: authorData.institution || authors[currentUserAuthorIndex].institution,
          role: authorData.role || authors[currentUserAuthorIndex].role
        };
      } else {
        // Only add the current user if this is a normal upload (not an external import)
        // For external imports (like arXiv), the user is just the uploader, not an author
        if (!externalUrl) {
          authors.push({
            id: authorId,
            name: authorData.name,
            institution: authorData.institution,
            role: authorData.role,
            isPlatformUser: true,
            isUploader: true
          });
        }
      }
    } else {
      // No additional authors provided - this is a normal single-author upload
      authors = [{
        id: authorId,
        name: authorData.name,
        institution: authorData.institution,
        role: authorData.role,
        isPlatformUser: true
      }];
    }
    
    // Create the paper
    const now = new Date().toISOString();
    const paperId = uuidv4();
    
    const { data: paperData, error: paperError } = await supabase
      .from('papers')
      .insert({
        id: paperId,
        title,
        abstract,
        author_id: authorId,
        category,
        keywords,
        current_version: "1.0",
        published_at: publishedAt || now,
        updated_at: now,
        views: 0,
        external_url: externalUrl || null,
        authors,
        // License information
        license: licenseData.license?.rawText || licenseData.license?.name || null,
        license_compliance_status: licenseData.licenseComplianceStatus,
        license_fetched_at: licenseData.licenseFetchedAt || null,
        license_validation_error: licenseData.licenseValidationError || null,
        // Visibility
        visibility: visibility,
        ai_review_count: 0,
        made_public_at: visibility === 'public' ? now : null
      })
      .select()
      .single();

    if (paperError) {
      adminError('Error creating paper:', paperError);
      
      if (paperError.code === '23505') {
        toast.error('A paper with this title already exists', {
          description: 'Please use a different title'
        });
      } else {
        toast.error('Failed to create paper', { 
          description: paperError.message 
        });
      }
      return null;
    }

    adminLog('Paper created successfully:', paperData);

    // Create the initial version
    const versionData = await createPaperVersion(
      paperId,
      title,
      abstract,
      fileUrl,
      externalUrl,
      publishedAt
    );

    if (!versionData) {
      return null;
    }

    // Trigger background processing for both arXiv and manual uploads
    const shouldTriggerBackgroundProcessing = 
      (externalUrl && isArxivUrl(externalUrl)) || // arXiv papers
      (fileUrl && !externalUrl); // manual PDF uploads

    if (shouldTriggerBackgroundProcessing) {
      try {
        let pdfUrl: string;
        
        if (externalUrl && isArxivUrl(externalUrl)) {
          // For arXiv papers, convert to PDF URL
          pdfUrl = convertArxivUrlToPdf(externalUrl);
          adminLog('Triggering background processing for arXiv paper:', pdfUrl);
        } else if (fileUrl) {
          // For manual uploads, use the file URL directly
          pdfUrl = fileUrl;
          adminLog('Triggering background processing for manual upload:', pdfUrl);
        } else {
          throw new Error('No valid PDF URL found for background processing');
        }
        
        // Seed processing jobs so the timeline appears immediately.
        await initProcessingJobs(paperId, DEFAULT_UPLOAD_STEPS);

        // Trigger both AI review and data extraction concurrently
        const [aiReviewPromise, dataExtractionPromise] = await Promise.allSettled([
          triggerAIReview(paperId, pdfUrl, title, abstract, versionData.version, visibility === 'private', category),
          triggerDataExtraction(paperId, pdfUrl, title, abstract, keywords, externalUrl, titleIsPlaceholderOverride)
        ]);
        
        // Bump per-paper AI review counter for user-uploaded papers (any
        // visibility) when the AI review trigger succeeded. Credit consumption
        // itself is handled atomically inside generate-ai-review.
        if (isUserUpload && aiReviewPromise.status === 'fulfilled') {
          await supabase.from('papers').update({
            ai_review_count: 1,
            last_ai_review_at: new Date().toISOString()
          }).eq('id', paperId);
        }


        // Log results
        if (aiReviewPromise.status === 'rejected') {
          adminError('Failed to trigger AI review:', aiReviewPromise.reason);
        } else {
          adminLog('AI review triggered successfully');
        }
        
        if (dataExtractionPromise.status === 'rejected') {
          adminError('Failed to trigger data extraction:', dataExtractionPromise.reason);
        } else {
          adminLog('Data extraction triggered successfully');
        }
        
      } catch (error) {
        adminError('Failed to trigger background processing:', error);
        // Don't fail the paper creation if background processing fails
      }
    }

    // Add to search indexing queue (only for public papers)
    if (visibility === 'public') {
      try {
        const paperUrl = `${SITE_URL}/paper/${paperId}`;
        
        const { error: queueError } = await supabase
          .from('search_indexing_queue')
          .insert({
            paper_id: paperId,
            paper_url: paperUrl,
            status: 'pending',
            scheduled_at: new Date().toISOString()
          });

        if (queueError) {
          adminError('Failed to add paper to search indexing queue:', queueError);
        } else {
          adminLog('Paper added to search indexing queue');
        }
      } catch (error) {
        adminError('Error adding to search indexing queue:', error);
        // Don't fail paper creation if queue insertion fails
      }
    }

    // Map the data to our Paper type
    const result = mapToPaper(paperData, [versionData]);
    
    // Make sure to set the authors correctly
    result.authors = authors;
    
    adminLog('Returning mapped paper:', result);
    return result;
    
  } catch (error: unknown) {
    adminError('Unexpected error in createPaper:', error);
    toast.error('An unexpected error occurred', {
      description: (error instanceof Error ? error.message : 'Could not create the paper due to a system error.')
    });
    return null;
  }
}

/**
 * Triggers AI review for papers (both arXiv and manual uploads)
 */
async function triggerAIReview(
  paperId: string,
  pdfUrl: string,
  title: string,
  abstract: string,
  version: string = '1.0',
  isPrivate: boolean = false,
  category?: string
): Promise<void> {
  try {
    adminLog(`Triggering AI review for paper ${paperId} with PDF: ${pdfUrl}`);
    
    // Get the bot user ID
    const botUserId = await ensureBotUserExists();
    if (!botUserId) {
      throw new Error('Could not get bot user ID for AI review generation');
    }

    // Call the AI review edge function
    const { data, error } = await supabase.functions.invoke('generate-ai-review', {
      body: {
        paperId,
        pdfUrl,
        title,
        abstract,
        reviewerId: botUserId,
        isPrivatePaperReview: isPrivate,
        paperVersion: version,
        paperCategory: category
      }
    });

    if (error) {
      adminError('AI review generation failed:', error);
      throw new Error(error.message);
    }

    if (isGdprObjectionSkip(data)) {
      adminLog('AI review skipped — GDPR objection on file');
      showAIObjectionToast();
      return data;
    }

    adminLog('AI review generation initiated successfully');
    return data;
  } catch (error) {
    adminError('Error in triggerAIReview:', error);
    throw error;
  }
}

/**
 * Triggers data extraction for papers (both arXiv and manual uploads)
 */
async function triggerDataExtraction(
  paperId: string,
  pdfUrl: string,
  title: string,
  abstract: string,
  currentKeywords: string[],
  externalUrl?: string,
  titleIsPlaceholderOverride?: boolean
) {
  try {
    // Detect if this is a private paper with placeholder metadata
    const extractionMode = abstract === 'Pending AI extraction' ? 'full' : 'standard';
    // The title is a placeholder if the user didn't provide one AND we're in full extraction mode
    const titleIsPlaceholder =
      typeof titleIsPlaceholderOverride === 'boolean'
        ? titleIsPlaceholderOverride
        : extractionMode === 'full';
    adminLog(`Triggering data extraction for paper ${paperId} (mode: ${extractionMode}, titleIsPlaceholder: ${titleIsPlaceholder}) with PDF: ${pdfUrl}`);

    // Call the extract-paper-data edge function
    const { data, error } = await supabase.functions.invoke('extract-paper-data', {
      body: {
        paperId,
        pdfUrl,
        title,
        abstract,
        currentKeywords: currentKeywords || [],
        externalUrl,
        extractionMode,
        titleIsPlaceholder
      }
    });

    if (error) {
      adminError('Data extraction failed:', error);
      throw new Error(error.message);
    }

    if (isGdprObjectionSkip(data)) {
      adminLog('Data extraction skipped — GDPR objection on file');
      showAIObjectionToast();
      return data;
    }

    adminLog('Data extraction initiated successfully');
    return data;
  } catch (error) {
    adminError('Error in triggerDataExtraction:', error);
    throw error;
  }
}
